import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  RotateCcw,
  Download,
  Search,
  Truck,
  Coins,
  CheckCircle2,
  TrendingUp,
  Clock,
  RefreshCw,
  PackageCheck,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileSpreadsheet,
  CheckSquare,
  Square,
} from 'lucide-react';
import {
  fetchShippingCa,
  type ShippingCaResponse,
  type ShippingCaStatusOption,
  type ShippingCaWooStatusOption,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd, int } from '../lib/format.js';
import { cn } from '../ui/cn.js';

const DEFAULT_SHIPARON_STATUSES: ShippingCaStatusOption[] = [
  { id: 2, label: 'Livré', count: 0 },
  { id: 1, label: 'En cours', count: 0 },
  { id: 0, label: 'En attente', count: 0 },
  { id: 99, label: 'Non reçu', count: 0 },
  { id: 31, label: 'Rtn définitif', count: 0 },
  { id: 7, label: 'Rtn client/agence', count: 0 },
  { id: 11, label: 'Rtn dépôt', count: 0 },
  { id: 6, label: 'Annulé/Supprimé', count: 0 },
  { id: 5, label: 'Rtn expéditeur', count: 0 },
  { id: 8, label: 'Au magasin/dépôt', count: 0 },
  { id: 20, label: 'À vérifier', count: 0 },
];

const DEFAULT_WOO_STATUSES: ShippingCaWooStatusOption[] = [
  { slug: 'completed', label: 'Terminée (Complétée)', count: 0 },
  { slug: 'en-cours-de-prepa', label: 'En cours de préparation', count: 0 },
  { slug: 'cancelled', label: 'Annulée', count: 0 },
  { slug: 'on-hold', label: 'En attente', count: 0 },
  { slug: 'unmatched', label: 'Sans commande (Non liée)', count: 0 },
];

function KpiCard(props: {
  label: string;
  value: string;
  sub: string;
  accent: string;
  icon?: React.ComponentType<{ className?: string }>;
}): React.ReactElement {
  const Icon = props.icon;
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-xs flex flex-col justify-between">
      <div className="flex items-center justify-between text-ink-ghost">
        <span className="text-[11px] font-bold uppercase tracking-wider">{props.label}</span>
        {Icon && <Icon className="h-4 w-4 shrink-0 opacity-70" />}
      </div>
      <div className="mt-2">
        <div className={cn('text-2xl font-black font-mono tracking-tight', props.accent)}>{props.value}</div>
        <div className="mt-1 text-xs text-ink-soft">{props.sub}</div>
      </div>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const clean = iso.split('T')[0];
  const parts = clean?.split('-');
  if (parts && parts.length === 3 && parts[0]?.length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return iso;
}

export function ShippingCaPage(): React.ReactElement {
  // Date presets helpers
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }, []);

  const monthStartStr = useMemo(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
  }, []);

  // Filter states
  const [carrier, setCarrier] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(monthStartStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [selectedWooStatuses, setSelectedWooStatuses] = useState<string[]>([]);
  const [customFeeTtc, setCustomFeeTtc] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [showDailyTable, setShowDailyTable] = useState<boolean>(true);
  const [statusesExpanded, setStatusesExpanded] = useState<boolean>(true);
  const [wooStatusesExpanded, setWooStatusesExpanded] = useState<boolean>(true);

  // Quick preset handlers
  const handlePreset = (
    preset:
      | 'today'
      | 'yesterday'
      | '7days'
      | '30days'
      | 'thisMonth'
      | 'lastMonth'
      | 'year2026'
      | 'year2025'
      | 'year2024'
      | 'year2023'
      | 'allTime',
  ) => {
    const now = new Date();
    const formatYmd = (d: Date) =>
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

    if (preset === 'today') {
      const s = formatYmd(now);
      setStartDate(s);
      setEndDate(s);
    } else if (preset === 'yesterday') {
      const y = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
      const s = formatYmd(y);
      setStartDate(s);
      setEndDate(s);
    } else if (preset === '7days') {
      const past = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 7));
      setStartDate(formatYmd(past));
      setEndDate(formatYmd(now));
    } else if (preset === '30days') {
      const past = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30));
      setStartDate(formatYmd(past));
      setEndDate(formatYmd(now));
    } else if (preset === 'thisMonth') {
      setStartDate(monthStartStr);
      setEndDate(todayStr);
    } else if (preset === 'lastMonth') {
      const firstDayLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const lastDayLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      setStartDate(formatYmd(firstDayLastMonth));
      setEndDate(formatYmd(lastDayLastMonth));
    } else if (preset === 'year2026') {
      setStartDate('2026-01-01');
      setEndDate(todayStr);
    } else if (preset === 'year2025') {
      setStartDate('2025-01-01');
      setEndDate('2025-12-31');
    } else if (preset === 'year2024') {
      setStartDate('2024-01-01');
      setEndDate('2024-12-31');
    } else if (preset === 'year2023') {
      setStartDate('2023-01-01');
      setEndDate('2023-12-31');
    } else if (preset === 'allTime') {
      setStartDate('2023-01-01');
      setEndDate(todayStr);
    }
    setPage(1);
  };

  const periodInvalid = startDate !== '' && endDate !== '' && startDate > endDate;

  const statusesParam = useMemo(() => {
    return selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined;
  }, [selectedStatuses]);

  const wooStatusesParam = useMemo(() => {
    return selectedWooStatuses.length > 0 ? selectedWooStatuses.join(',') : undefined;
  }, [selectedWooStatuses]);

  // Query
  const { data, isLoading, isFetching, refetch } = useQuery<ShippingCaResponse>({
    queryKey: ['shipping-ca', carrier, startDate, endDate, statusesParam, wooStatusesParam, customFeeTtc, search, page],
    queryFn: () =>
      fetchShippingCa({
        carrier,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        statuses: statusesParam,
        wooStatuses: wooStatusesParam,
        customFeeTtc: customFeeTtc.trim() ? customFeeTtc.trim() : undefined,
        search: search.trim() || undefined,
        page,
        limit: 50,
      }),
    enabled: !periodInvalid,
    staleTime: 30_000,
  });

  const kpis = data?.kpis;
  const couriers = data?.couriersList ?? [];
  const dailyBreakdown = data?.dailyBreakdown ?? [];
  const parcels = data?.parcels?.items ?? [];
  const totalParcelsCount = data?.parcels?.total ?? 0;
  const totalPages = data?.parcels?.totalPages ?? 1;

  // Merge available statuses with defaults to guarantee all 11 Shiparon statuses are present
  const statusOptions = useMemo(() => {
    const fromApi = data?.availableStatuses ?? [];
    const map = new Map<number, ShippingCaStatusOption>();
    for (const def of DEFAULT_SHIPARON_STATUSES) {
      map.set(def.id, def);
    }
    for (const s of fromApi) {
      map.set(s.id, s);
    }
    return Array.from(map.values()).sort((a, b) => {
      // Keep Livré (2) first, then sort by count descending
      if (a.id === 2) return -1;
      if (b.id === 2) return 1;
      return b.count - a.count;
    });
  }, [data?.availableStatuses]);

  // Dynamic WooCommerce order statuses
  const wooStatusOptions = useMemo(() => {
    const fromApi = data?.availableWooStatuses ?? [];
    const map = new Map<string, ShippingCaWooStatusOption>();
    for (const def of DEFAULT_WOO_STATUSES) {
      map.set(def.slug, def);
    }
    for (const s of fromApi) {
      map.set(s.slug, s);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.slug === 'completed') return -1;
      if (b.slug === 'completed') return 1;
      if (a.slug === 'en-cours-de-prepa') return -1;
      if (b.slug === 'en-cours-de-prepa') return 1;
      return b.count - a.count;
    });
  }, [data?.availableWooStatuses]);

  // Separate couriers by type for dropdown grouping
  const prestaCouriers = couriers.filter((c) => c.type === 'presta' || c.type === 'excel');
  const livreurCouriers = couriers.filter((c) => c.type === 'livreur');

  // Status toggling handlers
  const toggleStatus = (id: number) => {
    setSelectedStatuses((prev) => {
      if (prev.length === 0) {
        // If previously all were selected, unchecking this one means all others are checked
        return statusOptions.map((s) => s.id).filter((sid) => sid !== id);
      }
      if (prev.includes(id)) {
        const next = prev.filter((sid) => sid !== id);
        return next;
      }
      const next = [...prev, id];
      // If user checked all, reset to empty array (= all)
      if (next.length === statusOptions.length) return [];
      return next;
    });
    setPage(1);
  };

  const selectAllStatuses = () => {
    setSelectedStatuses([]);
    setPage(1);
  };

  const clearAllStatuses = () => {
    // Select an impossible ID to represent none selected
    setSelectedStatuses([-999]);
    setPage(1);
  };

  const selectDeliveredOnly = () => {
    setSelectedStatuses([2]);
    setPage(1);
  };

  const selectReturnsOnly = () => {
    setSelectedStatuses([4, 5, 6, 7, 11, 31]);
    setPage(1);
  };

  const selectInTransitOnly = () => {
    setSelectedStatuses([0, 1, 8, 20, 99]);
    setPage(1);
  };

  const isStatusChecked = (id: number): boolean => {
    if (selectedStatuses.length === 0) return true; // all checked by default
    return selectedStatuses.includes(id);
  };

  // WooCommerce status toggling handlers
  const toggleWooStatus = (slug: string) => {
    setSelectedWooStatuses((prev) => {
      if (prev.length === 0) {
        return wooStatusOptions.map((s) => s.slug).filter((s) => s !== slug);
      }
      if (prev.includes(slug)) {
        const next = prev.filter((s) => s !== slug);
        return next;
      }
      const next = [...prev, slug];
      if (next.length === wooStatusOptions.length) return [];
      return next;
    });
    setPage(1);
  };

  const selectAllWooStatuses = () => {
    setSelectedWooStatuses([]);
    setPage(1);
  };

  const clearAllWooStatuses = () => {
    setSelectedWooStatuses(['__none__']);
    setPage(1);
  };

  const selectCompletedWooOnly = () => {
    setSelectedWooStatuses(['completed']);
    setPage(1);
  };

  const selectInPrepaWooOnly = () => {
    setSelectedWooStatuses(['en-cours-de-prepa']);
    setPage(1);
  };

  const selectCancelledWooOnly = () => {
    setSelectedWooStatuses(['cancelled']);
    setPage(1);
  };

  const selectUnmatchedWooOnly = () => {
    setSelectedWooStatuses(['unmatched']);
    setPage(1);
  };

  const isWooStatusChecked = (slug: string): boolean => {
    if (selectedWooStatuses.length === 0) return true;
    return selectedWooStatuses.includes(slug);
  };

  // Export URLs
  const exportCsvUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (carrier) q.set('carrier', carrier);
    if (startDate) q.set('startDate', startDate);
    if (endDate) q.set('endDate', endDate);
    if (statusesParam) q.set('statuses', statusesParam);
    if (wooStatusesParam) q.set('wooStatuses', wooStatusesParam);
    if (customFeeTtc.trim()) q.set('customFeeTtc', customFeeTtc.trim());
    if (search.trim()) q.set('search', search.trim());
    return `/api/v1/shipping/ca/export?${q.toString()}`;
  }, [carrier, startDate, endDate, statusesParam, wooStatusesParam, customFeeTtc, search]);

  const exportExcelUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (carrier) q.set('carrier', carrier);
    if (startDate) q.set('startDate', startDate);
    if (endDate) q.set('endDate', endDate);
    if (statusesParam) q.set('statuses', statusesParam);
    if (wooStatusesParam) q.set('wooStatuses', wooStatusesParam);
    if (customFeeTtc.trim()) q.set('customFeeTtc', customFeeTtc.trim());
    if (search.trim()) q.set('search', search.trim());
    return `/api/v1/shipping/ca/export-excel?${q.toString()}`;
  }, [carrier, startDate, endDate, statusesParam, wooStatusesParam, customFeeTtc, search]);

  return (
    <div className="space-y-5 animate-fade pb-10">
      {/* Top Page Header */}
      <PageHeader
        index="S14"
        eyebrow="Gestion Logistique · CA Livraison"
        title="Chiffre d'Affaires Livraison & Encaissements Coursiers"
        purpose="Sélectionnez un transporteur ou un coursier et une période de dates pour analyser son chiffre d'affaires collecté (COD), ses frais de livraison, son taux de réussite et le net reversé."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <a href={exportExcelUrl} download>
              <Button variant="outline" size="sm" className="font-semibold shadow-2xs gap-1.5 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100">
                <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
                <span>Exporter Colis (Excel)</span>
              </Button>
            </a>
            <a href={exportCsvUrl} download>
              <Button variant="outline" size="sm" className="font-semibold shadow-2xs gap-1.5">
                <Download className="h-4 w-4 text-ink-soft" />
                <span>Exporter Colis (CSV)</span>
              </Button>
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              title="Rafraîchir les données"
            >
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin text-emerald-700')} />
            </Button>
          </div>
        }
      />

      {/* Primary Filters Control Bar */}
      <Panel className="p-4 space-y-3.5 border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
          {/* Carrier / Courier Selector */}
          <div className="flex items-center gap-2.5 min-w-[260px] flex-1 max-w-sm">
            <div className="p-2 bg-emerald-100/70 text-emerald-800 rounded-lg shrink-0">
              <Truck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <label htmlFor="carrier-select" className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                Coursier / Transporteur
              </label>
              <select
                id="carrier-select"
                value={carrier}
                onChange={(e) => {
                  setCarrier(e.target.value);
                  setPage(1);
                }}
                className="mt-0.5 w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-ink shadow-2xs focus:border-emerald-600 focus:outline-none"
              >
                <option value="all">Tous les transporteurs (Synthèse globale)</option>
                {prestaCouriers.length > 0 && (
                  <optgroup label="Sociétés de livraison (Prestataires)">
                    {prestaCouriers.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label} ({c.count.toLocaleString('fr-FR')} colis)
                      </option>
                    ))}
                  </optgroup>
                )}
                {livreurCouriers.length > 0 && (
                  <optgroup label="Livreurs & Tournées Dédiées">
                    {livreurCouriers.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label} ({c.count.toLocaleString('fr-FR')} colis)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>

          {/* Delivery Fee Input TTC per parcel */}
          <div className="flex items-center gap-2 bg-cream/40 p-2 rounded-lg border border-border">
            <Coins className="h-4 w-4 text-emerald-700 shrink-0" />
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                Frais livraison TTC / colis
              </label>
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  type="number"
                  step="0.100"
                  min="0"
                  value={customFeeTtc}
                  onChange={(e) => {
                    setCustomFeeTtc(e.target.value);
                    setPage(1);
                  }}
                  placeholder={kpis ? `${(kpis.deliveryPriceUnitTtc ?? (kpis.deliveryPriceUnitHt * (1 + kpis.vatRate))).toFixed(3)}` : '7.140'}
                  className="w-24 rounded-md border border-border/80 bg-card px-2 py-1 font-mono text-xs font-bold text-ink focus:border-emerald-600 focus:outline-none"
                />
                <span className="text-xs font-bold text-ink-soft">DT</span>
                {customFeeTtc && (
                  <button
                    type="button"
                    onClick={() => setCustomFeeTtc('')}
                    className="text-[10px] text-ink-ghost hover:text-rust-700 ml-1 underline cursor-pointer"
                    title="Réinitialiser au barème par défaut"
                  >
                    Barème
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Date Range Selection */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-cream/40 p-1 rounded-lg border border-border">
              <Calendar className="h-3.5 w-3.5 text-ink-ghost ml-1.5" />
              <label className="text-[11px] font-medium text-ink-soft">Du</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                className="rounded-md border border-border/80 bg-card px-2 py-1 font-mono text-xs text-ink focus:border-emerald-600 focus:outline-none"
              />
              <label className="text-[11px] font-medium text-ink-soft">Au</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                className="rounded-md border border-border/80 bg-card px-2 py-1 font-mono text-xs text-ink focus:border-emerald-600 focus:outline-none"
              />
            </div>

            {/* Quick Presets */}
            <div className="flex flex-wrap items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('today')}
                className={cn(startDate === todayStr && endDate === todayStr && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                Aujourd'hui
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('yesterday')}
              >
                Hier
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('7days')}
              >
                7 jours
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('thisMonth')}
                className={cn(startDate === monthStartStr && endDate === todayStr && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                Ce mois
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('lastMonth')}
              >
                Mois dernier
              </Button>
              <span className="text-border mx-1">|</span>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('year2026')}
                className={cn(startDate === '2026-01-01' && endDate === todayStr && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                2026
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('year2025')}
                className={cn(startDate === '2025-01-01' && endDate === '2025-12-31' && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                2025
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('year2024')}
                className={cn(startDate === '2024-01-01' && endDate === '2024-12-31' && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                2024
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('year2023')}
                className={cn(startDate === '2023-01-01' && endDate === '2023-12-31' && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                2023
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => handlePreset('allTime')}
                className={cn(startDate === '2023-01-01' && endDate === todayStr && 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold')}
              >
                Tout l'historique
              </Button>
            </div>
          </div>
        </div>

        {/* Dynamic Shiparon Statuses Checklist */}
        <div className="space-y-2 pt-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-ink uppercase tracking-wider">
                Statuts Shiparon ({selectedStatuses.length === 0 ? 'Tous cochés' : `${selectedStatuses.length} sélectionné(s)`}) :
              </span>
              <button
                type="button"
                onClick={() => setStatusesExpanded((p) => !p)}
                className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
              >
                {statusesExpanded ? 'Réduire' : 'Déplier'}
              </button>
            </div>

            {/* Quick Status Presets */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectAllStatuses}
                className={cn(selectedStatuses.length === 0 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
              >
                Tous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectDeliveredOnly}
                className={cn(selectedStatuses.length === 1 && selectedStatuses[0] === 2 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
              >
                Livrés
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectReturnsOnly}
                className={cn(selectedStatuses.length === 6 && selectedStatuses.includes(31) && 'bg-rust-50 font-bold text-rust-800 border-rust-300')}
              >
                Retours
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectInTransitOnly}
                className={cn(selectedStatuses.length === 5 && selectedStatuses.includes(1) && 'bg-sky-50 font-bold text-sky-800 border-sky-300')}
              >
                En cours / Transit
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={clearAllStatuses}
              >
                Aucun
              </Button>
            </div>
          </div>

          {statusesExpanded && (
            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-cream/30 rounded-xl border border-border/70 max-h-32 overflow-y-auto">
              {statusOptions.map((st) => {
                const checked = isStatusChecked(st.id);
                return (
                  <label
                    key={st.id}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors select-none',
                      checked
                        ? 'bg-card text-ink border-emerald-400/80 shadow-2xs font-semibold'
                        : 'bg-card/50 text-ink-ghost border-border hover:bg-card hover:text-ink-soft',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStatus(st.id)}
                      className="sr-only"
                    />
                    {checked ? (
                      <CheckSquare className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                    ) : (
                      <Square className="h-3.5 w-3.5 text-ink-ghost shrink-0" />
                    )}
                    <span>{st.label}</span>
                    {st.count > 0 && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cream text-ink-soft border border-border font-mono">
                        {st.count.toLocaleString('fr-FR')}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Dynamic WooCommerce Order Statuses Checklist */}
        <div className="space-y-2 pt-1 border-t border-border/50">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-ink uppercase tracking-wider">
                Statuts Commande WooCommerce ({selectedWooStatuses.length === 0 ? 'Tous cochés' : `${selectedWooStatuses.length} sélectionné(s)`}) :
              </span>
              <button
                type="button"
                onClick={() => setWooStatusesExpanded((p) => !p)}
                className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
              >
                {wooStatusesExpanded ? 'Réduire' : 'Déplier'}
              </button>
            </div>

            {/* Quick Woo Status Presets */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectAllWooStatuses}
                className={cn(selectedWooStatuses.length === 0 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
              >
                Tous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectCompletedWooOnly}
                className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'completed' && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
              >
                Complétées
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectInPrepaWooOnly}
                className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'en-cours-de-prepa' && 'bg-blue-50 font-bold text-blue-800 border-blue-300')}
              >
                En prépa
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectCancelledWooOnly}
                className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'cancelled' && 'bg-rose-50 font-bold text-rose-800 border-rose-300')}
              >
                Annulées
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={selectUnmatchedWooOnly}
                className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'unmatched' && 'bg-zinc-100 font-bold text-zinc-800 border-zinc-300')}
              >
                Sans commande
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={clearAllWooStatuses}
              >
                Aucun
              </Button>
            </div>
          </div>

          {wooStatusesExpanded && (
            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-cream/30 rounded-xl border border-border/70 max-h-32 overflow-y-auto">
              {wooStatusOptions.map((st) => {
                const checked = isWooStatusChecked(st.slug);
                return (
                  <label
                    key={st.slug}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors select-none',
                      checked
                        ? 'bg-card text-ink border-emerald-400/80 shadow-2xs font-semibold'
                        : 'bg-card/50 text-ink-ghost border-border hover:bg-card hover:text-ink-soft',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleWooStatus(st.slug)}
                      className="sr-only"
                    />
                    {checked ? (
                      <CheckSquare className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                    ) : (
                      <Square className="h-3.5 w-3.5 text-ink-ghost shrink-0" />
                    )}
                    <span>{st.label}</span>
                    {st.count > 0 && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cream text-ink-soft border border-border font-mono">
                        {st.count.toLocaleString('fr-FR')}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Search inside parcels */}
        <div className="pt-1">
          <div className="relative w-full max-w-md">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Rechercher code barre, commande, client, ville…"
              className="w-full h-8 pl-8 pr-3 text-xs bg-card border border-border rounded-lg text-ink placeholder:text-ink-ghost focus:border-emerald-600 focus:outline-none"
            />
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost pointer-events-none" />
          </div>
        </div>

        {periodInvalid && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 font-medium">
            Attention : la date de début doit être antérieure ou égale à la date de fin.
          </div>
        )}
      </Panel>

      {/* KPI Stats Strip */}
      {isLoading ? (
        <div className="py-12 text-center">
          <Loading label="Calcul du Chiffre d'Affaires Livraison en cours…" />
        </div>
      ) : kpis ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* 1. CA Encaissé (COD Livré) */}
          <KpiCard
            label="CA Encaissé (COD)"
            value={formatTnd(kpis.deliveredCod)}
            sub={`${int(kpis.deliveredCount)} colis livrés (${kpis.deliveryRatePct}%)`}
            accent="text-emerald-900"
            icon={Coins}
          />

          {/* 2. Frais Livraison */}
          <KpiCard
            label="Frais Livraison TTC"
            value={formatTnd(kpis.estimatedFeesTtc)}
            sub={`HT: ${formatTnd(kpis.estimatedFeesHt)} (${formatTnd(kpis.deliveryPriceUnitTtc)} TTC/colis)`}
            accent="text-amber-800"
            icon={Truck}
          />

          {/* 3. Net à Reverser */}
          <KpiCard
            label="Net à Reverser"
            value={formatTnd(kpis.netPayout)}
            sub="COD Encaissé − Frais TTC"
            accent="text-emerald-700 font-extrabold"
            icon={TrendingUp}
          />

          {/* 4. Colis en Retour */}
          <KpiCard
            label="Retours & Annulés"
            value={int(kpis.returnedCount)}
            sub={`Montant : ${formatTnd(kpis.returnedCod)}`}
            accent="text-rust-700"
            icon={RotateCcw}
          />

          {/* 5. Colis en Transit */}
          <KpiCard
            label="En Cours / Transit"
            value={int(kpis.inTransitCount)}
            sub={`Montant : ${formatTnd(kpis.inTransitCod)}`}
            accent="text-sky-700"
            icon={Clock}
          />

          {/* 6. Rapprochement Payé */}
          <KpiCard
            label="Payé / Déclaré"
            value={formatTnd(kpis.paidCod)}
            sub={`Reste à verser: ${formatTnd(kpis.unpaidCod)}`}
            accent="text-indigo-900"
            icon={PackageCheck}
          />
        </div>
      ) : null}

      {/* Daily Breakdown Section */}
      {dailyBreakdown.length > 0 && (
        <Panel className="p-4 space-y-3 border-border bg-card shadow-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-700" />
              <h2 className="text-sm font-bold text-ink">Évolution & Ventilation Journalière du CA</h2>
              <span className="text-[11px] font-semibold text-ink-ghost">({dailyBreakdown.length} jours d'activité)</span>
            </div>
            <button
              type="button"
              onClick={() => setShowDailyTable((p) => !p)}
              className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
            >
              {showDailyTable ? 'Masquer la ventilation' : 'Afficher la ventilation journalière'}
            </button>
          </div>

          {showDailyTable && (
            <div className="overflow-x-auto border border-border rounded-xl">
              <Table>
                <THead>
                  <Tr>
                    <Th className="text-left font-bold text-ink">Date</Th>
                    <Th className="text-right font-bold text-ink">Colis Livrés</Th>
                    <Th className="text-right font-bold text-emerald-950">COD Encaissé (DT)</Th>
                    <Th className="text-right font-bold text-ink">Retours</Th>
                    <Th className="text-right font-bold text-amber-900">Frais TTC (DT)</Th>
                    <Th className="text-right font-black text-ink">Net Reversé (DT)</Th>
                  </Tr>
                </THead>
                <TBody>
                  {dailyBreakdown.map((d) => (
                    <Tr key={d.date} className="hover:bg-cream/40 transition-colors">
                      <Td className="font-semibold font-mono text-ink text-xs">{formatDate(d.date)}</Td>
                      <Td className="text-right tabular-nums text-xs">{int(d.deliveredCount)}</Td>
                      <Td className="text-right font-bold tabular-nums text-xs text-emerald-950">
                        {formatTnd(d.deliveredCod)}
                      </Td>
                      <Td className="text-right tabular-nums text-xs text-rust-700">{int(d.returnedCount)}</Td>
                      <Td className="text-right tabular-nums text-xs text-amber-900">{formatTnd(d.feesTtc ?? (d.feesHt * 1.19))}</Td>
                      <Td className="text-right font-bold tabular-nums text-xs text-ink">{formatTnd(d.netPayout)}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </Panel>
      )}

      {/* Detailed Parcels Table */}
      <Panel className="p-4 space-y-3.5 border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-emerald-700" />
            <h2 className="text-sm font-bold text-ink">Bordereau Détaillé des Colis</h2>
            <span className="text-[11px] font-semibold text-ink-ghost bg-cream px-2 py-0.5 rounded-full border border-border">
              {totalParcelsCount.toLocaleString('fr-FR')} colis trouvés
            </span>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-ink-soft">
                Page <strong className="text-ink">{page}</strong> sur <strong className="text-ink">{totalPages}</strong>
              </span>
              <Button
                variant="outline"
                size="xs"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span>Précédent</span>
              </Button>
              <Button
                variant="outline"
                size="xs"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <span>Suivant</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        {parcels.length === 0 ? (
          <div className="py-12 text-center text-xs text-ink-faint">
            Aucun colis correspondant aux critères sélectionnés.
          </div>
        ) : (
          <TableShell className="max-h-[calc(100vh-320px)] overflow-y-auto border border-border rounded-xl">
            <Table>
              <THead className="sticky top-0 bg-card z-10 shadow-2xs">
                <Tr>
                  <Th className="text-left font-bold text-ink">Code Barre</Th>
                  <Th className="text-left font-bold text-ink">Commande</Th>
                  <Th className="text-left font-bold text-ink">Date Événement</Th>
                  <Th className="text-left font-bold text-ink">Client</Th>
                  <Th className="text-left font-bold text-ink">Destination</Th>
                  <Th className="text-left font-bold text-ink">Livreur / Agence</Th>
                  <Th className="text-center font-bold text-ink">Statut</Th>
                  <Th className="text-right font-bold text-ink">Montant COD (DT)</Th>
                  <Th className="text-right font-bold text-ink">Frais TTC</Th>
                  <Th className="text-center font-bold text-ink">Paiement</Th>
                </Tr>
              </THead>
              <TBody>
                {parcels.map((p) => {
                  const cleanRef = p.orderRef ? p.orderRef.replace(/^[#\s]+/, '').replace(/\s*[-–—]?\s*Note:.*$/i, '').trim() : '';
                  return (
                    <Tr key={p.id} className="hover:bg-cream/40 transition-colors">
                      <Td className="font-mono text-xs font-bold text-ink select-all">{p.codeBarre}</Td>
                      <Td className="text-xs">
                        <div className="font-mono font-bold text-ink">
                          {cleanRef ? `#${cleanRef}` : <span className="text-ink-ghost">—</span>}
                        </div>
                        {p.wooStatusLabel && (
                          <span
                            className={cn(
                              'inline-block mt-0.5 px-1.5 py-0.2 rounded text-[10px] font-sans font-semibold border',
                              p.wooStatus === 'completed'
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                : p.wooStatus === 'en-cours-de-prepa'
                                  ? 'bg-blue-50 text-blue-800 border-blue-300'
                                  : p.wooStatus === 'cancelled'
                                    ? 'bg-rose-50 text-rose-800 border-rose-300'
                                    : p.wooStatus === 'on-hold'
                                      ? 'bg-amber-50 text-amber-800 border-amber-300'
                                      : 'bg-cream text-ink-soft border-border',
                            )}
                          >
                            {p.wooStatusLabel}
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs text-ink-soft whitespace-nowrap">{formatDate(p.dateEvent)}</Td>
                      <Td className="text-xs font-semibold text-ink max-w-[160px]">
                        <div className="truncate" title={p.clientNom ?? ''}>
                          {p.clientNom || '—'}
                        </div>
                        {p.clientTel && (
                          <span className="block text-[11px] font-mono text-ink-ghost">{p.clientTel}</span>
                        )}
                      </Td>
                      <Td className="text-xs text-ink-soft max-w-[140px]">
                        <div className="truncate" title={p.destination ?? ''}>
                          {p.destination || '—'}
                        </div>
                      </Td>
                      <Td className="text-xs text-ink max-w-[140px] truncate font-medium">
                        {p.courierName}
                      </Td>
                      <Td className="text-center whitespace-nowrap">
                        {p.status === 'delivered' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                            <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                            Livré
                          </span>
                        ) : p.status === 'returned' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rust-100 text-rust-900 border border-rust-300">
                            <RotateCcw className="h-3 w-3 text-rust-700" />
                            {p.statusLabel || 'Retour'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-900 border border-sky-300">
                            <Clock className="h-3 w-3 text-sky-700" />
                            {p.statusLabel || 'En cours'}
                          </span>
                        )}
                      </Td>
                      <Td className="text-right font-mono font-bold text-xs tabular-nums text-emerald-950">
                        {formatTnd(p.prixCod)}
                      </Td>
                      <Td className="text-right font-mono text-xs tabular-nums text-ink-soft">
                        {(p.feeTtc ?? p.feeHt) > 0 ? formatTnd(p.feeTtc ?? p.feeHt) : '0,000 DT'}
                      </Td>
                      <Td className="text-center">
                        {p.isPaid ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                            Payé
                          </span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-600 border border-zinc-200">
                            En attente
                          </span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          </TableShell>
        )}
      </Panel>
    </div>
  );
}
